import React from "react";
import { CreateGroupResourceRequest, CreateGroupRuleRequest, CreateRelatedGroupRequest, GroupDTO } from "@/generated-client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Spinner } from "@/components/ui/spinner";
import { Localizer } from "@/main/react/Types";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import SimpleDialog, { openSimpleDialog, SimpleDialogFooter } from "@/main/react/Dialog/SimpleDialog";
import { showYesNoDialog } from "@/main/react/Dialog/YesNoDialog";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import { ClientFactory } from "@/main/common/ClientFactory";
import DateFormatter from "@/main/common/DateFormatter";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import { Pencil, Trash2, X, Sparkles, Link2, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import { showCreateGroupDialog } from "@/main/react/Dialog/UpdateGroupSettingsDialog";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import {validateGroupItemValues} from "@/main/validation/groupItem";
import {REQUEST_VALIDATION_LIMITS} from "@/main/validation/request";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import { getHideSensitiveMediaPreference, getSensitivePreferenceMode } from "@/main/common/SensitivePreference";
import { getDisplayUsername, hasDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import { GroupResourceLike, GroupRuleLike, RelatedGroupLike } from "@/main/react/group/GroupGovernanceTypes";

export type GroupKind = "rule" | "resource" | "related";
export type GroupMode = "create" | "edit";

type GroupItemSeed = {
    id?: number;
    title?: string;
    description?: string | null;
    url?: string | null;
    relatedGroupPublicId?: string;
    sortOrder?: number | null;
};

type DialogProps = {
    groupPublicId: string;
    localizer: Localizer;
    onSaved?: (result: GroupItemDialogSaveResult) => void;
    mode?: GroupMode;
    item?: GroupItemSeed | null;
    initialRelatedCandidates?: {
        leader?: GroupDTO[] | null;
        member?: GroupDTO[] | null;
    };
};

export type GroupItemDialogSaveResult =
    | { kind: "rule"; mode: GroupMode; item: GroupRuleLike; deleted?: false }
    | { kind: "resource"; mode: GroupMode; item: GroupResourceLike; deleted?: false }
    | { kind: "related"; mode: GroupMode; item: RelatedGroupLike; deleted?: false }
    | { kind: GroupKind; mode: GroupMode; deleted: true; itemId: number };

function unwrapResponseData<T>(value: T | { data?: T }): T {
    if (value && typeof value === "object" && "data" in value && (value as { data?: T }).data !== undefined) {
        return (value as { data: T }).data;
    }

    return value as T;
}

function submitKey(kind: GroupKind, mode: GroupMode): LocalizationKey {
    if (mode === "edit") {
        switch (kind) {
            case "rule":
                return "group.rule.updated";
            case "resource":
                return "group.resource.updated";
            case "related":
                return "group.related.updated";
        }
    }

    switch (kind) {
        case "rule":
            return "group.rule.saved";
        case "resource":
            return "group.resource.saved";
        case "related":
            return "group.related.saved";
    }
}

function failedKey(kind: GroupKind, mode: GroupMode): LocalizationKey {
    if (mode === "edit") {
        switch (kind) {
            case "rule":
                return "group.rule.update_failed";
            case "resource":
                return "group.resource.update_failed";
            case "related":
                return "group.related.update_failed";
        }
    }

    switch (kind) {
        case "rule":
            return "group.rule.save_failed";
        case "resource":
            return "group.resource.save_failed";
        case "related":
            return "group.related.save_failed";
    }
}

function deleteSuccessKey(kind: GroupKind): LocalizationKey {
    switch (kind) {
        case "rule":
            return "group.rule.deleted";
        case "resource":
            return "group.resource.deleted";
        case "related":
            return "group.related.deleted";
    }
}

function deleteConfirmKey(kind: GroupKind): LocalizationKey {
    switch (kind) {
        case "rule":
            return "group.rule.delete_confirm";
        case "resource":
            return "group.resource.delete_confirm";
        case "related":
            return "group.related.delete_confirm";
    }
}

function titleKey(kind: GroupKind, mode: GroupMode): LocalizationKey {
    if (mode === "edit") {
        switch (kind) {
            case "rule":
                return "group.rule.edit_modal_title";
            case "resource":
                return "group.resource.edit_modal_title";
            case "related":
                return "group.related.edit_modal_title";
        }
    }

    switch (kind) {
        case "rule":
            return "group.rule.modal_title";
        case "resource":
            return "group.resource.modal_title";
        case "related":
            return "group.related.modal_title";
    }
}

function saveButtonKey(kind: GroupKind, mode: GroupMode): LocalizationKey {
    if (mode === "edit") {
        switch (kind) {
            case "rule":
                return "group.rule.update";
            case "resource":
                return "group.resource.update";
            case "related":
                return "group.related.update";
        }
    }

    switch (kind) {
        case "rule":
            return "group.rule.save";
        case "resource":
            return "group.resource.save";
        case "related":
            return "group.related.save";
    }
}

export function SelectableRelatedGroupCard({
    group,
    localizer,
    disabled = false,
    submitting = false,
    onSelect,
}: {
    group: GroupDTO;
    localizer: Localizer;
    disabled?: boolean;
    submitting?: boolean;
    onSelect?: () => Promise<void> | void;
}) {
    const groupWithFlags = group as GroupDTO & { sensitive?: boolean | null };
    const df = new DateFormatter(localizer);
    const createdLabel = group.createdAt ? df.timeSince(group.createdAt) : null;
    const isSensitive = Boolean(groupWithFlags.sensitive);
    const sensitivePreference = getSensitivePreferenceMode();
    const hideSensitiveMedia = getHideSensitiveMediaPreference();

    const handleClick = async (e?: React.SyntheticEvent) => {
        try {
            e?.preventDefault();
            e?.stopPropagation();
        } catch {
            /* ignore */
        }

        if (disabled || !onSelect) return;
        await onSelect();
    };

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            await handleClick(e);
        }
    };

    const warningCopy = getMaskedContentWarningCopy(localizer, { isSensitive });

    return (
        <div
            className={`relative w-full rounded-xl border border-border p-3 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 transition-colors ${disabled ? "cursor-not-allowed opacity-70" : "card-hover cursor-pointer"}`}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            <div className="absolute right-3 top-3 z-10">
                {submitting ? <Spinner className="h-5 w-5 text-neutral" /> : <UsersRound className="h-4 w-4 text-primary" aria-hidden="true" />}
            </div>

            <div className="flex items-start gap-3">
                {group.thumbnailUrl ? (
                    <SensitiveMediaGuard
                        isSensitive={isSensitive}
                        hideSensitiveMedia={hideSensitiveMedia}
                        sensitivePreference={sensitivePreference}
                        variant="avatar"
                        overlayStacking="below-link-overlay"
                        badgeLabel={warningCopy.badgeLabel}
                        label={warningCopy.label}
                        helperText={warningCopy.helperText}
                        revealText={warningCopy.revealText}
                        className="rounded-full"
                    >
                        <ProfileImage localizer={localizer}
                            imageUrl={group.thumbnailUrl}
                            username={group.name ?? ""}
                            mode="group"
                            hoverable={false}
                            className="size-12 shrink-0"
                        />
                    </SensitiveMediaGuard>
                ) : null}

                <div className="min-w-0 flex-1 pr-6">
                    <div className="truncate text-base font-semibold text-text-main">
                        <span className="inline-flex max-w-full flex-wrap items-center gap-2">
                            <span className="truncate">{group.name}</span>
                            {isSensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                        </span>
                    </div>

                    {(hasDisplayUsername(group.creatorUsername) || createdLabel) ? (
                        <AuthorAttribution localizer={localizer}
                            name={getDisplayUsername(group.creatorUsername, localizer)}
                            imageUrl={group.creatorProfileImageUrl}
                            avatarSeed={group.creatorUsername}
                            details={createdLabel ? <time dateTime={group.createdAt ?? undefined}>{createdLabel}</time> : null}
                            className="mt-1 text-xs"
                            avatarClassName="size-5"
                        />
                    ) : null}

                    <p className="mt-2 line-clamp-3 text-sm text-neutral-text">
                        {group.description ?? localizer("group.no_description")}
                    </p>

                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-neutral-text">
                        <span>{group.discussionsCount ?? 0} {localizer("group.discussions")}</span>
                        <span>·</span>
                        <span>{group.privateAccess ? localizer("group.private") : localizer("group.public")}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function GroupItemDialog({
    kind,
    groupPublicId,
    localizer,
    onSaved,
    mode = "create",
    item,
    initialRelatedCandidates,
    open,
    onOpenChange,
}: DialogProps & { kind: GroupKind; open: boolean; onOpenChange: (open: boolean) => void }) {
    const [saving, setSaving] = React.useState(false);
    const [loadingRelatedCandidates, setLoadingRelatedCandidates] = React.useState(false);
    const [leaderRelatedCandidates, setLeaderRelatedCandidates] = React.useState<GroupDTO[] | null>(null);
    const [memberRelatedCandidates, setMemberRelatedCandidates] = React.useState<GroupDTO[] | null>(null);
    const [relatedCandidatesTab, setRelatedCandidatesTab] = React.useState<"leader" | "member">("leader");
    const [submittingRelatedGroupPublicId, setSubmittingRelatedGroupPublicId] = React.useState<string | null>(null);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const [title, setTitle] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [url, setUrl] = React.useState("");
    const [relatedGroupPublicId, setRelatedGroupPublicId] = React.useState("");
    const groupApi = React.useMemo(() => ClientFactory.getAjaxGroupRestControllerApi(), []);
    const itemId = item?.id;
    const isCreateRelatedDialog = kind === "related" && mode === "create";
    const hasInitialRelatedCandidates = initialRelatedCandidates != null;
    const groupsDialogToastErrorMessage = localizer("groups.modal.toast_error");

    React.useEffect(() => {
        if (!open) return;
        setTitle(item?.title ?? "");
        setDescription(item?.description ?? "");
        setUrl(item?.url ?? "");
        setRelatedGroupPublicId(item?.relatedGroupPublicId ?? "");
        setSubmittingRelatedGroupPublicId(null);
        setValidationError(null);
    }, [item, open]);

    React.useEffect(() => {
        if (!open || !isCreateRelatedDialog) return;

        if (hasInitialRelatedCandidates) {
            const leaderItems = Array.isArray(initialRelatedCandidates?.leader) ? initialRelatedCandidates.leader : [];
            const memberItems = Array.isArray(initialRelatedCandidates?.member) ? initialRelatedCandidates.member : [];

            setLoadingRelatedCandidates(false);
            setLeaderRelatedCandidates(leaderItems);
            setMemberRelatedCandidates(memberItems);

            if (leaderItems.length > 0) {
                setRelatedCandidatesTab("leader");
            } else if (memberItems.length > 0) {
                setRelatedCandidatesTab("member");
            }

            return;
        }

        let active = true;
        setLoadingRelatedCandidates(true);
        setLeaderRelatedCandidates(null);
        setMemberRelatedCandidates(null);
        setRelatedCandidatesTab("leader");

        Promise.all([
            groupApi.listRelatedGroupCandidates(groupPublicId, "leader"),
            groupApi.listRelatedGroupCandidates(groupPublicId, "member"),
        ])
            .then(([leaderResp, memberResp]) => {
                if (!active) return;

                const leaderItems = Array.isArray(leaderResp.data) ? leaderResp.data : [];
                const memberItems = Array.isArray(memberResp.data) ? memberResp.data : [];

                setLeaderRelatedCandidates(leaderItems);
                setMemberRelatedCandidates(memberItems);

                if (leaderItems.length > 0) {
                    setRelatedCandidatesTab("leader");
                } else if (memberItems.length > 0) {
                    setRelatedCandidatesTab("member");
                }
            })
            .catch(() => {
                if (!active) return;
                toast.error(groupsDialogToastErrorMessage);
                setLeaderRelatedCandidates([]);
                setMemberRelatedCandidates([]);
            })
            .finally(() => {
                if (active) {
                    setLoadingRelatedCandidates(false);
                }
            });

        return () => {
            active = false;
        };
    }, [groupApi, groupPublicId, groupsDialogToastErrorMessage, hasInitialRelatedCandidates, initialRelatedCandidates, isCreateRelatedDialog, open]);

    const buildRulePayload = React.useCallback((): CreateGroupRuleRequest => ({
        title: title.trim(),
        description: description.trim() || undefined,
    }), [description, title]);

    const buildResourcePayload = React.useCallback((): CreateGroupResourceRequest => ({
        title: title.trim(),
        description: description.trim() || undefined,
        url: url.trim(),
    }), [description, title, url]);

    const buildRelatedPayload = React.useCallback((nextRelatedGroupPublicId?: string): CreateRelatedGroupRequest => ({
        relatedGroupPublicId: (nextRelatedGroupPublicId ?? relatedGroupPublicId).trim(),
    }), [relatedGroupPublicId]);

    const submitDialog = React.useCallback(async (nextRelatedGroupPublicId?: string) => {
        if (saving) return;
        if (mode === "edit" && itemId == null) {
            toast.error(localizer(failedKey(kind, mode)));
            return;
        }

        const trimmedTitle = title.trim();
        const trimmedDescription = description.trim();
        const trimmedUrl = url.trim();
        const trimmedRelatedGroupPublicId = (nextRelatedGroupPublicId ?? relatedGroupPublicId).trim();

        const nextValidationError = validateGroupItemValues(kind, {
            title: trimmedTitle,
            description: trimmedDescription,
            url: trimmedUrl,
            relatedGroupPublicId: trimmedRelatedGroupPublicId,
        }, localizer);

        setValidationError(nextValidationError);
        if (nextValidationError) {
            toast.error(nextValidationError);
            return;
        }

        setSaving(true);
        setSubmittingRelatedGroupPublicId(nextRelatedGroupPublicId ?? null);

        try {
            let saveResult: GroupItemDialogSaveResult;

            if (kind === "rule") {
                const savedRuleResponse = mode === "edit"
                    ? await groupApi.updateGroupRule(groupPublicId, itemId as number, buildRulePayload())
                    : await groupApi.createGroupRule(groupPublicId, buildRulePayload());
                const savedRule = unwrapResponseData<GroupRuleLike>(savedRuleResponse as GroupRuleLike | { data?: GroupRuleLike });

                saveResult = {
                    kind,
                    mode,
                    item: savedRule,
                };
            } else if (kind === "resource") {
                const savedResourceResponse = mode === "edit"
                    ? await groupApi.updateGroupResource(groupPublicId, itemId as number, buildResourcePayload())
                    : await groupApi.createGroupResource(groupPublicId, buildResourcePayload());
                const savedResource = unwrapResponseData<GroupResourceLike>(savedResourceResponse as GroupResourceLike | { data?: GroupResourceLike });

                saveResult = {
                    kind,
                    mode,
                    item: savedResource,
                };
            } else {
                const savedRelatedGroupResponse = mode === "edit"
                    ? await groupApi.updateRelatedGroup(groupPublicId, itemId as number, buildRelatedPayload(nextRelatedGroupPublicId))
                    : await groupApi.createRelatedGroup(groupPublicId, buildRelatedPayload(nextRelatedGroupPublicId));
                const savedRelatedGroup = unwrapResponseData<RelatedGroupLike>(savedRelatedGroupResponse as RelatedGroupLike | { data?: RelatedGroupLike });

                saveResult = {
                    kind,
                    mode,
                    item: savedRelatedGroup,
                };
            }

            setValidationError(null);
            toast.success(localizer(submitKey(kind, mode)));
            onSaved?.(saveResult);
            onOpenChange(false);
        } catch (err) {
            if (isHandledAuthSessionError(err)) {
                return;
            }
            toast.error(localizer(failedKey(kind, mode)));
        } finally {
            setSaving(false);
            setSubmittingRelatedGroupPublicId(null);
        }
    }, [
        buildRelatedPayload,
        buildResourcePayload,
        buildRulePayload,
        groupApi,
        groupPublicId,
        itemId,
        kind,
        mode,
        onOpenChange,
        onSaved,
        saving,
        localizer,
    ]);

    const handleSave = async () => {
        await submitDialog();
    };

    const handleDelete = async () => {
        if (saving || mode !== "edit" || itemId == null) return;

        showYesNoDialog({
            localizer,
            title: localizer("literal.delete"),
            message: <div>{localizer(deleteConfirmKey(kind))}</div>,
            yesLabel: localizer("literal.delete"),
            yesIcon: Trash2,
            noLabel: localizer("literal.cancel"),
            noIcon: X,
            destructive: true,
            onYes: async () => {
                setSaving(true);
                try {
                    if (kind === "rule") {
                        await groupApi.deleteGroupRule(groupPublicId, itemId);
                    } else if (kind === "resource") {
                        await groupApi.deleteGroupResource(groupPublicId, itemId);
                    } else {
                        await groupApi.deleteRelatedGroup(groupPublicId, itemId);
                    }
                    toast.success(localizer(deleteSuccessKey(kind)));
                    onSaved?.({
                        kind,
                        mode,
                        deleted: true,
                        itemId,
                    });
                    onOpenChange(false);
                } catch (err) {
                    if (isHandledAuthSessionError(err)) {
                        return;
                    }
                    // YesNoDialog surfaces the failure banner on re-throw; don't also toast.
                    throw err;
                } finally {
                    setSaving(false);
                }
            },
        });
    };

    const titleLabel = kind === "rule"
        ? localizer("group.rule.title_label")
        : kind === "resource"
            ? localizer("group.resource.title_label")
            : localizer("group.related.public_id_label");

    const titlePlaceholder = kind === "rule"
        ? localizer("group.rule.title_placeholder")
        : kind === "resource"
            ? localizer("group.resource.title_placeholder")
            : localizer("group.related.public_id_placeholder");

    const titleValue = kind === "related" ? relatedGroupPublicId : title;
    const setTitleValue = kind === "related" ? setRelatedGroupPublicId : setTitle;

    const openCreateGroup = React.useCallback(() => {
        onOpenChange(false);
        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                showCreateGroupDialog(localizer);
            }, 0);
        }
    }, [localizer, onOpenChange]);

    const renderRelatedCandidateContent = (items: GroupDTO[] | null, source: "leader" | "member") => {
        if (loadingRelatedCandidates) {
            return (
                <div className="flex w-full items-center justify-center py-6">
                    <Spinner className="h-10 w-10 text-neutral" />
                </div>
            );
        }

        if (items && items.length > 0) {
            return (
                <div className="flex max-h-[55vh] w-full flex-col gap-2 overflow-y-auto no-scrollbar pr-1">
                    {items.map((group) => (
                        <SelectableRelatedGroupCard
                            key={group.publicId ?? `${group.groupId ?? group.name}`}
                            group={group}
                            localizer={localizer}
                            disabled={saving || !group.publicId}
                            submitting={submittingRelatedGroupPublicId === group.publicId}
                            onSelect={() => submitDialog(group.publicId ?? undefined)}
                        />
                    ))}
                </div>
            );
        }

        const emptyTitle = source === "leader"
            ? localizer("group.related.empty.leader.title")
            : localizer("group.related.empty.member.title");
        const emptyDescription = source === "leader"
            ? localizer("group.related.empty.leader.description")
            : localizer("group.related.empty.member.description");

        return (
            <PaginatedListEmptyState
                icon={UsersRound}
                title={emptyTitle}
                description={emptyDescription}
                action={{
                    label: localizer("groups.modal.new_group"),
                    onClick: openCreateGroup,
                    icon: UsersRound,
                }}
            />
        );
    };

    if (isCreateRelatedDialog) {
        return (
            <SimpleDialog
                open={open}
                onOpenChange={onOpenChange}
                title={<div>{localizer(titleKey(kind, mode))}</div>}
                width="2xl"
            >
                <div className="space-y-4">
                    {validationError ? <div className="text-sm text-destructive-text">{validationError}</div> : null}


                    <Tabs className="w-full" value={relatedCandidatesTab} onValueChange={(value) => setRelatedCandidatesTab(value as "leader" | "member")}>
                        <TabsList variant={"line"} className="w-full justify-center">
                            <TabsTrigger value="leader">{localizer("groups.modal.leader_section")}</TabsTrigger>
                            <TabsTrigger value="member">{localizer("groups.modal.member_section")}</TabsTrigger>
                        </TabsList>

                        <TabsContent value="leader" className="w-full max-w-full">
                            {renderRelatedCandidateContent(leaderRelatedCandidates, "leader")}
                        </TabsContent>

                        <TabsContent value="member" className="w-full max-w-full">
                            {renderRelatedCandidateContent(memberRelatedCandidates, "member")}
                        </TabsContent>
                    </Tabs>

                </div>
                <SimpleDialogFooter>
                    <CtaButton icon={X} type="button" variant="outline" text={localizer("literal.cancel")} onClick={() => onOpenChange(false)} disabled={saving} />
                </SimpleDialogFooter>
            </SimpleDialog>
        );
    }

    return (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div>{localizer(titleKey(kind, mode))}</div>}
            width="2xl"
        >
            <div className="space-y-4">
                {validationError ? <div className="text-sm text-destructive-text">{validationError}</div> : null}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-text-main">{titleLabel}</label>
                    <Input
                        value={titleValue}
                        onChange={(e) => setTitleValue(e.target.value)}
                        placeholder={titlePlaceholder}
                        maxLength={kind === "related" ? REQUEST_VALIDATION_LIMITS.publicIdLength : REQUEST_VALIDATION_LIMITS.groupOrSeriesName.maxLength}
                        disabled={saving}
                    />
                </div>

                {kind !== "related" ? (
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-text-main">
                            {kind === "rule" ? localizer("group.rule.description_label") : localizer("group.resource.description_label")}
                        </label>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={kind === "rule" ? localizer("group.rule.description_placeholder") : localizer("group.resource.description_placeholder")}
                            disabled={saving}
                            rows={4}
                            maxLength={REQUEST_VALIDATION_LIMITS.longText.maxLength}
                        />
                    </div>
                ) : null}

                {kind === "resource" ? (
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-text-main">{localizer("group.resource.url_label")}</label>
                        <Input
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder={localizer("group.resource.url_placeholder")}
                            maxLength={REQUEST_VALIDATION_LIMITS.url.maxLength}
                            disabled={saving}
                        />
                    </div>
                ) : null}


            </div>
            <SimpleDialogFooter className="items-center justify-between sm:justify-between">
                <div>
                    {mode === "edit" && itemId != null ? (
                        <CtaButton
                            icon={Trash2}
                            type="button"
                            variant="destructive"
                            text={localizer("literal.delete")}
                            onClick={handleDelete}
                            disabled={saving}
                            extraClass=""
                        />
                    ) : null}
                </div>
                <div className="flex items-center gap-2">
                    <CtaButton icon={X} type="button" variant="outline" text={localizer("literal.cancel")} onClick={() => onOpenChange(false)} disabled={saving} />
                    <CtaButton icon={mode === "edit" ? Pencil : kind === "rule" ? Sparkles : kind === "resource" ? Link2 : UsersRound} type="button" variant="primary" text={localizer(saveButtonKey(kind, mode))} onClick={handleSave} disabled={saving} />
                </div>
            </SimpleDialogFooter>
        </SimpleDialog>
    );
}

function openGroupItemDialog(
    kind: GroupKind,
    groupPublicId: string,
    localizer: Localizer,
    mode: GroupMode,
    item?: GroupItemSeed | null,
    onSaved?: (result: GroupItemDialogSaveResult) => void,
    dialogOptions?: Pick<DialogProps, "initialRelatedCandidates">
) {
    return openSimpleDialog(({ open, onOpenChange }) => (
        <GroupItemDialog
            kind={kind}
            groupPublicId={groupPublicId}
            localizer={localizer}
            onSaved={onSaved}
            mode={mode}
            item={item}
            initialRelatedCandidates={dialogOptions?.initialRelatedCandidates}
            open={open}
            onOpenChange={onOpenChange}
        />
    ));
}

export function showCreateGroupRuleDialog(groupPublicId: string, localizer: Localizer, onSaved?: (result: GroupItemDialogSaveResult) => void) {
    return openGroupItemDialog("rule", groupPublicId, localizer, "create", null, onSaved);
}

export function showEditGroupRuleDialog(groupPublicId: string, rule: GroupItemSeed, localizer: Localizer, onSaved?: (result: GroupItemDialogSaveResult) => void) {
    return openGroupItemDialog("rule", groupPublicId, localizer, "edit", rule, onSaved);
}

export function showCreateGroupResourceDialog(groupPublicId: string, localizer: Localizer, onSaved?: (result: GroupItemDialogSaveResult) => void) {
    return openGroupItemDialog("resource", groupPublicId, localizer, "create", null, onSaved);
}

export function showEditGroupResourceDialog(groupPublicId: string, resource: GroupItemSeed, localizer: Localizer, onSaved?: (result: GroupItemDialogSaveResult) => void) {
    return openGroupItemDialog("resource", groupPublicId, localizer, "edit", resource, onSaved);
}

export function showCreateGroupRelatedGroupDialog(
    groupPublicId: string,
    localizer: Localizer,
    onSaved?: (result: GroupItemDialogSaveResult) => void,
    dialogOptions?: Pick<DialogProps, "initialRelatedCandidates">
) {
    return openGroupItemDialog("related", groupPublicId, localizer, "create", null, onSaved, dialogOptions);
}

export function showEditGroupRelatedGroupDialog(groupPublicId: string, relatedGroup: GroupItemSeed, localizer: Localizer, onSaved?: (result: GroupItemDialogSaveResult) => void) {
    return openGroupItemDialog("related", groupPublicId, localizer, "edit", relatedGroup, onSaved);
}
